import { useEffect, useRef } from 'react';

/**
 * Minimal local Dialog primitive — Tailwind-styled fixed overlay + centered
 * card with Escape + backdrop click dismissal (configurable), inline focus
 * trap, and focus restoration. First UI primitive in `web/`; no third-party
 * dep on purpose (see docs/designs/2026-06-19-admin-user-management-design.md
 * decision #6). Do not grow this into a kit.
 */
export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** When false, Escape and backdrop click do NOT close the dialog. Defaults true. */
  dismissible?: boolean;
  children: React.ReactNode;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({ open, onClose, title, dismissible = true, children }: DialogProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Save/restore focus + focus first focusable on open.
  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Focus first focusable element inside the card on mount.
    const focusFirst = () => {
      const card = cardRef.current;
      if (!card) return;
      const focusables = card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        card.focus();
      }
    };
    // Defer to next tick so children have mounted.
    const t = window.setTimeout(focusFirst, 0);

    return () => {
      window.clearTimeout(t);
      // Restore focus to whatever opened the dialog.
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open]);

  // Escape + focus-trap key handling.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismissible) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key === 'Tab') {
        const card = cardRef.current;
        if (!card) return;
        const focusables = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
          (el) => !el.hasAttribute('disabled'),
        );
        if (focusables.length === 0) {
          e.preventDefault();
          card.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !card.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  const handleBackdropClick = () => {
    if (dismissible) onClose();
  };

  return (
    <div
      data-testid="dialog-root"
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="presentation"
    >
      <div
        data-testid="dialog-backdrop"
        className="absolute inset-0 bg-black/60"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        ref={cardRef}
        data-testid="dialog-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative z-10 w-full max-w-md rounded-lg border border-gray-700 bg-gray-800 p-6 shadow-xl outline-none"
      >
        <h2 className="mb-4 text-xl font-semibold text-white" data-testid="dialog-title">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
